<template>
  <button :type="type" :disabled="disabled || loading" :class="classes">
    <svg
      v-if="loading"
      class="animate-spin h-4 w-4 shrink-0"
      :class="iconColorClass"
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
      <path
        class="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
      />
    </svg>
    <component
      :is="icon"
      v-else-if="icon && iconPosition === 'leading'"
      class="shrink-0"
      :class="iconSizeClass"
      aria-hidden="true"
    />
    <span
      v-if="$slots.default"
      :class="['inline-flex items-center gap-1.5', { 'sr-only': iconOnly }]"
    >
      <slot />
    </span>
    <component
      :is="icon"
      v-if="icon && iconPosition === 'trailing' && !loading"
      class="shrink-0"
      :class="iconSizeClass"
      aria-hidden="true"
    />
  </button>
</template>

<script>
export default {
  name: 'BaseButton',
  props: {
    variant: {
      type: String,
      default: 'primary',
      validator: (v) =>
        ['primary', 'secondary', 'ghost', 'danger', 'success', 'subtle'].includes(v),
    },
    size: {
      type: String,
      default: 'sm',
      validator: (v) => ['xs', 'sm', 'md'].includes(v),
    },
    type: {
      type: String,
      default: 'button',
    },
    icon: {
      type: [Object, Function],
      default: null,
    },
    iconOnly: {
      type: Boolean,
      default: false,
    },
    iconPosition: {
      type: String,
      default: 'leading',
      validator: (v) => ['leading', 'trailing'].includes(v),
    },
    block: {
      type: Boolean,
      default: false,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    classes() {
      const base =
        'inline-flex flex-nowrap items-center justify-center gap-1.5 font-semibold rounded-lg transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-offset-1 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap'

      const sizes = {
        xs: this.iconOnly ? 'p-1.5 text-xs min-h-[2rem]' : 'text-xs px-3 py-1.5 min-h-[2rem]',
        sm: this.iconOnly ? 'p-2 text-sm min-h-[2.125rem]' : 'text-sm px-3 py-2 min-h-[2.125rem]',
        md: this.iconOnly
          ? 'p-2.5 text-sm min-h-[2.375rem]'
          : 'text-sm px-4 py-2.5 min-h-[2.375rem]',
      }

      const variants = {
        primary: 'bg-primary text-white shadow-sm hover:bg-primaryDark focus:ring-primary/50',
        secondary:
          'bg-white text-gray-700 ring-1 ring-inset ring-gray-300 shadow-sm hover:bg-gray-50 focus:ring-primary/40',
        ghost: 'bg-transparent text-gray-600 hover:bg-gray-100 focus:ring-gray-300',
        danger:
          'bg-white text-red-600 ring-1 ring-inset ring-red-200 hover:bg-red-50 focus:ring-red-300',
        success: 'bg-success text-white shadow-sm hover:opacity-90 focus:ring-success/50',
        subtle: 'bg-primary/10 text-primaryAccent hover:bg-primary/20 focus:ring-primary/40',
      }

      return [base, sizes[this.size], variants[this.variant], this.block ? 'w-full' : '']
    },
    iconSizeClass() {
      return this.size === 'xs' ? 'h-3.5 w-3.5' : 'h-4 w-4'
    },
    iconColorClass() {
      return ['primary', 'success'].includes(this.variant) ? 'text-white' : 'text-current'
    },
  },
}
</script>
